/**
 * 同期状態の表示（SPEC §2.1）。
 * オフライン中・未送信あり・送信失敗ありのときだけ出す。
 * 上限超過で失敗扱いになったものはここから手動再送できる。
 */
import { useState, type ReactNode } from 'react';
import type { QueueOp } from '../lib/db';

export interface SyncBarProps {
  online: boolean;
  pending: number;
  failed: QueueOp[];
  onRetry: () => void;
  onDiscard: (seq: number) => void;
}

export function SyncBar({ online, pending, failed, onRetry, onDiscard }: SyncBarProps): ReactNode {
  const [open, setOpen] = useState(false);

  if (online && pending === 0 && failed.length === 0) return null;

  const tone = failed.length > 0 ? 'error' : 'muted';

  return (
    <div
      className={`border-b px-4 py-2 text-sm ${
        tone === 'error'
          ? 'border-red-500/30 bg-red-950/40 text-red-200'
          : 'border-ink-700 bg-ink-850 text-zinc-400'
      }`}
    >
      <div className="mx-auto flex max-w-3xl items-center gap-2">
        <span
          className={`size-2 shrink-0 rounded-full ${
            failed.length > 0 ? 'bg-red-400' : online ? 'bg-amber-400' : 'bg-zinc-500'
          }`}
        />
        <span className="min-w-0 flex-1 truncate">
          {!online && 'オフライン — 操作は端末に保存し、復帰時に送信します'}
          {online && failed.length === 0 && pending > 0 && `未送信 ${pending} 件を送信中…`}
          {failed.length > 0 && `送信できていない操作が ${failed.length} 件あります`}
        </span>

        {failed.length > 0 && (
          <>
            <button
              type="button"
              className="shrink-0 rounded-lg px-2 py-1 text-xs font-bold text-red-200 hover:bg-white/10"
              onClick={() => setOpen((v) => !v)}
            >
              {open ? '閉じる' : '詳細'}
            </button>
            <button
              type="button"
              className="shrink-0 rounded-lg bg-red-500/20 px-2.5 py-1 text-xs font-bold
                text-red-100 hover:bg-red-500/30"
              onClick={onRetry}
            >
              再送
            </button>
          </>
        )}
      </div>

      {open && failed.length > 0 && (
        <ul className="mx-auto mt-2 max-w-3xl space-y-1.5">
          {failed.map((op) => (
            <li
              key={op.seq}
              className="flex items-start gap-2 rounded-lg bg-black/25 px-3 py-2 text-xs"
            >
              <span className="min-w-0 flex-1">
                <span className="block font-semibold">{op.label}</span>
                <span className="block text-red-300/70">{op.lastError}</span>
              </span>
              <button
                type="button"
                className="shrink-0 rounded px-1.5 py-0.5 text-red-300/80 underline underline-offset-2
                  hover:text-red-100"
                onClick={() => onDiscard(op.seq!)}
              >
                破棄
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
